import { useId } from "react";

/**
 * The gilded charms — the child's interests as small storybook illustrations
 * drifting around the cover (see Incantation's Motifs).
 *
 * These replaced a set of thin line-glyphs that read as UI icons and clashed
 * with the dimensional book. The charms share ONE visual language so they feel
 * like they came from the same illustrator's hand:
 *
 *  - a faint warm halo wash behind each, so it sits ON the page like pressed
 *    foil rather than floating over it;
 *  - plump, rounded silhouettes filled with soft warm gradients drawn from the
 *    Hearth palette (cream, gold, terracotta, sage, a night blue for accents);
 *  - fine ink detailing and a glint or tiny gold sparkle for life.
 *
 * All inline SVG — no asset files, nothing fetched, nothing generated at
 * runtime. Anything unmapped falls back to a warm sparkle, so every child's
 * loves find a home on the page. Gradient ids come from useId() so multiple
 * charms never collide.
 */

/* The charm palette — literal values matching app.css's Hearth tokens (the
   charms only ever appear on the daytime cover, so no night variants). */
const INK = "#4a3a2a";
const CREAM = "#fff6e4";
const GOLD_LT = "#f2c879";
const GOLD = "#d69a3e";
const GOLD_DEEP = "#b47a2a";
const TERRA_LT = "#dd8a5c";
const TERRA = "#c96f47";
const TERRA_DEEP = "#9c4f2e";
const SAGE_LT = "#a8bd97";
const SAGE = "#8fa781";
const SAGE_DEEP = "#5f7a54";
const BLUSH = "#f6ddc9";
const NIGHT = "#2b3454";

/** The faint pressed-foil wash behind every charm. */
function Halo({ id }: { id: string }) {
  return (
    <>
      <radialGradient id={id}>
        <stop offset="0" stopColor={GOLD} stopOpacity="0.2" />
        <stop offset="0.65" stopColor={GOLD} stopOpacity="0.07" />
        <stop offset="1" stopColor={GOLD} stopOpacity="0" />
      </radialGradient>
      <circle cx="24" cy="24" r="22" fill={`url(#${id})`} />
    </>
  );
}

/** A concave four-point sparkle — the charm set's shared grace note. */
function Sparkle({
  x,
  y,
  s,
  fill = GOLD,
  opacity = 0.9,
}: {
  x: number;
  y: number;
  s: number;
  fill?: string;
  opacity?: number;
}) {
  const q = s * 0.22;
  const d = `M${x} ${y - s}Q${x + q} ${y - q} ${x + s} ${y}Q${x + q} ${y + q} ${x} ${y + s}Q${x - q} ${y + q} ${x - s} ${y}Q${x - q} ${y - q} ${x} ${y - s}Z`;
  return <path d={d} fill={fill} opacity={opacity} />;
}

function Lin({
  id,
  from,
  to,
  x1 = 0.25,
  y1 = 0,
  x2 = 0.55,
  y2 = 1,
}: {
  id: string;
  from: string;
  to: string;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}) {
  return (
    <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
      <stop offset="0" stopColor={from} />
      <stop offset="1" stopColor={to} />
    </linearGradient>
  );
}

const svgProps = {
  viewBox: "0 0 48 48",
  "aria-hidden": true,
} as const;

/* ---- the charms ---------------------------------------------------------- */

function StarCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={GOLD_LT} to={GOLD_DEEP} />
      <path
        d="M24 6l5.2 11.2 12.2 1.6-9 8.4 2.4 12L24 34l-10.8 5.6 2.4-12-9-8.4 12.2-1.6L24 6z"
        fill={`url(#${u}a)`}
        stroke={`url(#${u}a)`}
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <ellipse
        cx="19.5"
        cy="16.5"
        rx="3.1"
        ry="4.6"
        transform="rotate(24 19.5 16.5)"
        fill={CREAM}
        opacity="0.55"
      />
      <Sparkle x={40} y={36} s={3.6} />
      <Sparkle x={8.5} y={10} s={2.6} opacity={0.7} />
    </svg>
  );
}

function MoonCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from="#fbe9b4" to="#e0a94f" />
      {/* Crescent opening top-right; the face sleeps on the thick side. */}
      <path
        d="M40 29A16 16 0 1 1 19 8a13 13 0 0 0 21 21z"
        fill={`url(#${u}a)`}
        stroke={GOLD_DEEP}
        strokeWidth="1"
        strokeOpacity="0.35"
        strokeLinejoin="round"
      />
      <circle cx="21.5" cy="14.5" r="2" fill={GOLD_DEEP} opacity="0.28" />
      <circle cx="14.5" cy="21.5" r="1.4" fill={GOLD_DEEP} opacity="0.24" />
      <path
        d="M13.6 28.6q2.4 2.5 4.8 0"
        fill="none"
        stroke={INK}
        strokeWidth="1.9"
        strokeLinecap="round"
        opacity="0.85"
      />
      <path
        d="M17.4 34.4q1.9 1.7 3.8 0"
        fill="none"
        stroke={INK}
        strokeWidth="1.7"
        strokeLinecap="round"
        opacity="0.7"
      />
      <circle cx="11" cy="32.6" r="1.8" fill={TERRA} opacity="0.28" />
      <Sparkle x={36} y={9.5} s={3.4} />
      <Sparkle x={41.5} y={18} s={2.3} opacity={0.7} />
    </svg>
  );
}

function PlanetCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={GOLD_LT} to={TERRA} />
      <g transform="rotate(-18 24 25)">
        {/* back half of the ring, behind the sphere */}
        <path
          d="M4.5 25A19.5 6.4 0 0 1 43.5 25"
          fill="none"
          stroke={GOLD_DEEP}
          strokeWidth="2.4"
          strokeLinecap="round"
          opacity="0.75"
        />
        <circle cx="24" cy="25" r="13" fill={`url(#${u}a)`} />
        {/* soft surface bands */}
        <path
          d="M12.8 20.5q11 4.6 22.4 0"
          fill="none"
          stroke={CREAM}
          strokeWidth="2.6"
          strokeLinecap="round"
          opacity="0.4"
        />
        <path
          d="M13.6 29.5q10.2 4 20.8 0"
          fill="none"
          stroke={TERRA_DEEP}
          strokeWidth="2.2"
          strokeLinecap="round"
          opacity="0.3"
        />
        {/* front half of the ring */}
        <path
          d="M4.5 25A19.5 6.4 0 0 0 43.5 25"
          fill="none"
          stroke={GOLD}
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      </g>
      <ellipse
        cx="19"
        cy="18"
        rx="3.4"
        ry="2.2"
        transform="rotate(-24 19 18)"
        fill={CREAM}
        opacity="0.5"
      />
      <Sparkle x={40.5} y={9.5} s={3} />
      <circle cx="8.5" cy="11" r="1.5" fill={GOLD_LT} opacity="0.9" />
    </svg>
  );
}

function RocketCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={CREAM} to={BLUSH} />
      <Lin id={`${u}b`} from={TERRA_LT} to={TERRA_DEEP} />
      <Lin id={`${u}c`} from={GOLD_LT} to={TERRA} />
      <g transform="rotate(9 24 24)">
        {/* fins */}
        <path
          d="M16.2 26.5c-3.5 1.5-5.7 4.4-6.4 8.6 3.6-.5 6.6-2 8.8-4.4z"
          fill={`url(#${u}b)`}
        />
        <path
          d="M31.8 26.5c3.5 1.5 5.7 4.4 6.4 8.6-3.6-.5-6.6-2-8.8-4.4z"
          fill={`url(#${u}b)`}
        />
        {/* body */}
        <path
          d="M24 3.5c6.2 4.6 8.8 10.4 8.8 17.4 0 4.4-1 8.4-2.9 11.9H18.1c-1.9-3.5-2.9-7.5-2.9-11.9 0-7 2.6-12.8 8.8-17.4z"
          fill={`url(#${u}a)`}
          stroke={TERRA_DEEP}
          strokeWidth="1"
          strokeOpacity="0.25"
        />
        {/* nose cone */}
        <path
          d="M24 3.5c3.5 2.6 5.9 5.7 7.3 9.4H16.7c1.4-3.7 3.8-6.8 7.3-9.4z"
          fill={`url(#${u}b)`}
        />
        {/* porthole */}
        <circle cx="24" cy="20.5" r="4.7" fill={NIGHT} stroke={GOLD} strokeWidth="2" />
        <circle cx="22.4" cy="18.9" r="1.4" fill={CREAM} opacity="0.85" />
        {/* skirt band */}
        <path d="M18.1 32.8h11.8" stroke={GOLD} strokeWidth="2.4" strokeLinecap="round" />
        {/* flame */}
        <path
          d="M24 35.8c2.7 2.4 3.5 5.3 2.4 8.6-1-.8-1.8-1.7-2.4-2.7-.6 1-1.4 1.9-2.4 2.7-1.1-3.3-.3-6.2 2.4-8.6z"
          fill={`url(#${u}c)`}
        />
      </g>
      <Sparkle x={9} y={13} s={2.8} opacity={0.75} />
      <Sparkle x={40.5} y={42} s={2.4} opacity={0.7} />
    </svg>
  );
}

function HeartCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={TERRA_LT} to={TERRA_DEEP} />
      <path
        d="M24 40.5S9.6 31.6 9.6 21.4A8.6 8.6 0 0 1 24 15.1a8.6 8.6 0 0 1 14.4 6.3C38.4 31.6 24 40.5 24 40.5z"
        fill={`url(#${u}a)`}
        stroke={`url(#${u}a)`}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <ellipse
        cx="17"
        cy="20.5"
        rx="2.6"
        ry="4"
        transform="rotate(28 17 20.5)"
        fill={CREAM}
        opacity="0.55"
      />
      <Sparkle x={38.5} y={12} s={3.2} />
      <Sparkle x={9} y={36.5} s={2.2} opacity={0.7} />
    </svg>
  );
}

function FlowerCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={BLUSH} to={TERRA_LT} />
      <Lin id={`${u}b`} from={GOLD_LT} to={GOLD_DEEP} />
      {/* stem + leaf first, behind the bloom */}
      <path
        d="M24 28c0 4.6-.2 9.2-.6 13.8"
        fill="none"
        stroke={SAGE_DEEP}
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <ellipse
        cx="18.6"
        cy="35.5"
        rx="4.4"
        ry="2.4"
        transform="rotate(-32 18.6 35.5)"
        fill={SAGE}
      />
      {[0, 72, 144, 216, 288].map((r) => (
        <ellipse
          key={r}
          cx="24"
          cy="13"
          rx="5.3"
          ry="8"
          transform={`rotate(${r} 24 21.5)`}
          fill={`url(#${u}a)`}
          stroke={TERRA}
          strokeWidth="0.8"
          strokeOpacity="0.35"
        />
      ))}
      <circle cx="24" cy="21.5" r="4.7" fill={`url(#${u}b)`} />
      <circle cx="22.6" cy="20.2" r="0.9" fill={CREAM} opacity="0.8" />
      <Sparkle x={38} y={10.5} s={2.8} opacity={0.8} />
    </svg>
  );
}

function FishCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={GOLD_LT} to={TERRA} x1={0} y1={0.2} x2={1} y2={0.8} />
      {/* tail */}
      <path
        d="M37.5 25c2.6-2.9 5.1-4.7 7.7-5.5-.9 1.9-1.4 3.8-1.4 5.5s.5 3.6 1.4 5.5c-2.6-.8-5.1-2.6-7.7-5.5z"
        fill={TERRA_DEEP}
      />
      {/* dorsal fin */}
      <path d="M22.5 15.2c1.7-3 4.1-4.9 7.2-5.6-.8 2.3-1.1 4.3-1 6.2z" fill={TERRA} />
      {/* body */}
      <path
        d="M8 25c4.4-7 10.2-10.5 16.4-10.5 6.3 0 11 3.9 14.1 10.5-3.1 6.6-7.8 10.5-14.1 10.5C18.2 35.5 12.4 32 8 25z"
        fill={`url(#${u}a)`}
      />
      {/* gill + scales, whisper-thin */}
      <path
        d="M20 18.5c2.3 3.9 2.3 9.1 0 13"
        fill="none"
        stroke={TERRA_DEEP}
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.4"
      />
      <path
        d="M27 19.5c1.9 3.3 1.9 7.7 0 11M32 21c1.4 2.4 1.4 5.6 0 8"
        fill="none"
        stroke={TERRA_DEEP}
        strokeWidth="1"
        strokeLinecap="round"
        opacity="0.25"
      />
      <circle cx="15.2" cy="22.8" r="1.9" fill={INK} />
      <circle cx="14.6" cy="22.2" r="0.6" fill="#fff" opacity="0.9" />
      {/* bubbles */}
      <circle cx="40.5" cy="11.5" r="1.9" fill="none" stroke={GOLD} strokeWidth="1.2" opacity="0.7" />
      <circle cx="44" cy="16.5" r="1.2" fill="none" stroke={GOLD} strokeWidth="1" opacity="0.55" />
      <Sparkle x={9.5} y={38} s={2.4} opacity={0.7} />
    </svg>
  );
}

function TreeCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={SAGE_LT} to={SAGE_DEEP} />
      <Lin id={`${u}b`} from="#a5713f" to="#7a4e2c" />
      {/* trunk */}
      <path
        d="M24 26c1 0 1.7.2 1.7.2l.8 13.6c0 1-1.1 1.7-2.5 1.7s-2.5-.7-2.5-1.7l.8-13.6s.7-.2 1.7-.2z"
        fill={`url(#${u}b)`}
      />
      {/* canopy: three soft crowns */}
      <circle cx="16" cy="19.5" r="8.6" fill={`url(#${u}a)`} />
      <circle cx="32" cy="19.5" r="8.6" fill={`url(#${u}a)`} />
      <circle cx="24" cy="12.5" r="9" fill={`url(#${u}a)`} />
      <ellipse cx="24" cy="21.5" rx="11.5" ry="6.5" fill={`url(#${u}a)`} />
      <ellipse cx="24" cy="24.5" rx="9.5" ry="3.2" fill={SAGE_DEEP} opacity="0.35" />
      {/* golden fruit */}
      <circle cx="17.5" cy="16.5" r="1.7" fill={GOLD_LT} />
      <circle cx="29.5" cy="21.5" r="1.7" fill={GOLD_LT} />
      <circle cx="24.5" cy="10.5" r="1.5" fill={GOLD_LT} />
      {/* grass tufts */}
      <path
        d="M15 41.5q2-2.4 4-.6M29 41.5q2-2.4 4-.6"
        fill="none"
        stroke={SAGE}
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.8"
      />
      <Sparkle x={40} y={34} s={2.4} opacity={0.7} />
    </svg>
  );
}

function TrainCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={TERRA_LT} to={TERRA_DEEP} />
      <Lin id={`${u}b`} from="#b85f3c" to={TERRA_DEEP} />
      {/* steam puffs */}
      <circle cx="14.5" cy="8.5" r="3.4" fill={CREAM} opacity="0.95" />
      <circle cx="19.5" cy="6" r="2.3" fill={CREAM} opacity="0.8" />
      <circle cx="23.5" cy="4.5" r="1.5" fill={CREAM} opacity="0.6" />
      {/* chimney + cab + boiler */}
      <rect x="12" y="12" width="5" height="7" rx="1.6" fill={`url(#${u}b)`} />
      <rect x="26" y="12.5" width="12.5" height="16.5" rx="2.6" fill={`url(#${u}b)`} />
      <rect x="28.4" y="15.5" width="7.6" height="6.6" rx="1.8" fill={NIGHT} />
      <circle cx="30.4" cy="17.5" r="1" fill={CREAM} opacity="0.8" />
      <rect x="7" y="17.5" width="20.5" height="11.5" rx="5.4" fill={`url(#${u}a)`} />
      {/* boiler face plate + lamp */}
      <path d="M7.8 23.2a4.8 4.8 0 0 1 4.6-5" fill="none" stroke={CREAM} strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
      <circle cx="9.8" cy="23.2" r="1.6" fill={GOLD_LT} stroke={GOLD_DEEP} strokeWidth="0.8" />
      {/* running board */}
      <rect x="6" y="29" width="33.5" height="3.2" rx="1.6" fill={INK} />
      {/* cow-catcher */}
      <path d="M6.5 32.2l-3.4 5.4h6.4z" fill={`url(#${u}b)`} />
      {/* wheels */}
      {[13.5, 23, 33].map((x) => (
        <g key={x}>
          <circle cx={x} cy="36.4" r="3.9" fill={INK} />
          <circle cx={x} cy="36.4" r="1.5" fill={GOLD} />
        </g>
      ))}
      {/* gold trim */}
      <path d="M8.5 20.5h17" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" opacity="0.75" />
      <Sparkle x={42.5} y={9} s={2.8} opacity={0.75} />
    </svg>
  );
}

function MountainCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={SAGE_LT} to={SAGE_DEEP} />
      <Lin id={`${u}b`} from={TERRA_LT} to={TERRA_DEEP} />
      {/* a small warm sun behind the range */}
      <circle cx="33.5" cy="13.5" r="4.6" fill={GOLD_LT} stroke={GOLD} strokeWidth="1.2" />
      {/* back peak */}
      <path d="M27 36L37.5 17l8 19z" fill={`url(#${u}b)`} />
      <path d="M37.5 17l2.9 5.2-2.9 2.4-2.8-2.4z" fill={CREAM} opacity="0.9" />
      {/* front peak */}
      <path d="M3.5 36L16.5 11l13 25z" fill={`url(#${u}a)`} />
      <path
        d="M16.5 11l3.6 7-2.1 2.3-1.5-1.6-1.5 1.6-2.1-2.3z"
        fill={CREAM}
        opacity="0.95"
      />
      {/* ground line + grass */}
      <path
        d="M4 38.5h40"
        stroke={SAGE_DEEP}
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.45"
      />
      <Sparkle x={8.5} y={12.5} s={2.6} opacity={0.75} />
      <Sparkle x={42} y={30} s={2.2} opacity={0.65} />
    </svg>
  );
}

function DinoCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={SAGE_LT} to={SAGE_DEEP} />
      {/* tail */}
      <path
        d="M35.5 26c3.4-.3 6.4-1.9 8.9-4.9.3 2.7-.4 5.1-2.1 7.2-1.7 2-3.9 3.2-6.8 3.5z"
        fill={`url(#${u}a)`}
      />
      {/* neck (behind the body) */}
      <path
        d="M19.5 29C15.4 25.2 14 18 16.2 10.8"
        fill="none"
        stroke={`url(#${u}a)`}
        strokeWidth="6.6"
        strokeLinecap="round"
      />
      {/* legs */}
      {[20, 25.2, 30.4, 35.2].map((x) => (
        <rect key={x} x={x} y="33.5" width="3.8" height="6.8" rx="1.9" fill={SAGE_DEEP} />
      ))}
      {/* body + belly */}
      <ellipse cx="27.5" cy="29.5" rx="11" ry="7.4" fill={`url(#${u}a)`} />
      <ellipse cx="27.5" cy="32.4" rx="7.8" ry="3.6" fill={CREAM} opacity="0.5" />
      {/* freckles */}
      <circle cx="31" cy="25.6" r="1.3" fill={SAGE_DEEP} opacity="0.55" />
      <circle cx="35.2" cy="28.4" r="1" fill={SAGE_DEEP} opacity="0.45" />
      <circle cx="26.5" cy="24.8" r="0.9" fill={SAGE_DEEP} opacity="0.45" />
      {/* head */}
      <ellipse
        cx="17"
        cy="9.5"
        rx="4.8"
        ry="3.7"
        transform="rotate(-14 17 9.5)"
        fill={`url(#${u}a)`}
      />
      <circle cx="15.6" cy="8.6" r="0.95" fill={INK} />
      <circle cx="15.3" cy="8.3" r="0.35" fill="#fff" />
      <path
        d="M13.2 11.2q1.6 1.3 3.2.5"
        fill="none"
        stroke={INK}
        strokeWidth="1.1"
        strokeLinecap="round"
        opacity="0.7"
      />
      <Sparkle x={40.5} y={12.5} s={3} />
      <Sparkle x={7.5} y={20} s={2.2} opacity={0.7} />
    </svg>
  );
}

function PetCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={TERRA_LT} to={TERRA_DEEP} />
      {/* head with ear notches */}
      <path
        d="M24 41.5c-7.2 0-13.1-3.5-15.6-9.3-1.9-4.5-2.1-10.6-.5-18.2l7.9 5.6c2.6-1.4 5.4-2.1 8.2-2.1s5.6.7 8.2 2.1l7.9-5.6c1.6 7.6 1.4 13.7-.5 18.2-2.5 5.8-8.4 9.3-15.6 9.3z"
        fill={`url(#${u}a)`}
      />
      {/* inner ears */}
      <path d="M9.7 16.1l5.4 3.8-4.1 2.9z" fill={TERRA_DEEP} opacity="0.8" />
      <path d="M38.3 16.1l-5.4 3.8 4.1 2.9z" fill={TERRA_DEEP} opacity="0.8" />
      {/* muzzle */}
      <ellipse cx="24" cy="33.5" rx="8.6" ry="6.4" fill={CREAM} />
      {/* happy closed eyes */}
      <path
        d="M15.8 26.2q2.3 2.7 4.6 0M27.6 26.2q2.3 2.7 4.6 0"
        fill="none"
        stroke={INK}
        strokeWidth="2.1"
        strokeLinecap="round"
      />
      {/* nose + mouth */}
      <path
        d="M24 33.6l-2.4-2.1c.6-.9 1.4-1.4 2.4-1.4s1.8.5 2.4 1.4z"
        fill={INK}
      />
      <path
        d="M24 33.6v1.6m0 0q-1.5 1.9-3.2.9m3.2-.9q1.5 1.9 3.2.9"
        fill="none"
        stroke={INK}
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      {/* blush */}
      <circle cx="13.4" cy="30.5" r="2" fill={TERRA} opacity="0.35" />
      <circle cx="34.6" cy="30.5" r="2" fill={TERRA} opacity="0.35" />
      <Sparkle x={41} y={9.5} s={2.8} opacity={0.8} />
    </svg>
  );
}

function ButterflyCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={GOLD_LT} to={TERRA} />
      <Lin id={`${u}b`} from={TERRA_LT} to={TERRA_DEEP} />
      {[1, -1].map((m) => (
        <g key={m} transform={m === -1 ? "translate(48 0) scale(-1 1)" : undefined}>
          {/* upper wing */}
          <path
            d="M22.4 21.8C18 14.6 11.4 11 6.3 12.7c-2 .7-2.6 2.6-1.7 5 1.9 5.3 8 8.8 15.5 8.3z"
            fill={`url(#${u}a)`}
          />
          {/* lower wing */}
          <path
            d="M21.8 27.4c-6.7-.7-11.7 1.5-13.8 5.7-.9 1.9-.1 3.4 1.9 3.7 4.7.8 9.4-2.1 12.2-8.1z"
            fill={`url(#${u}b)`}
          />
          {/* wing spots */}
          <circle cx="11.5" cy="18.2" r="1.9" fill={CREAM} opacity="0.75" />
          <circle cx="14.5" cy="31.5" r="1.3" fill={CREAM} opacity="0.6" />
        </g>
      ))}
      {/* body + head + antennae */}
      <path d="M24 17.5v16.5" stroke={INK} strokeWidth="3.2" strokeLinecap="round" />
      <circle cx="24" cy="14.8" r="2.2" fill={INK} />
      <path
        d="M22.9 13.2c-1.6-2.3-3.6-3.5-5.8-3.6M25.1 13.2c1.6-2.3 3.6-3.5 5.8-3.6"
        fill="none"
        stroke={INK}
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="16.9" cy="9.5" r="0.9" fill={INK} />
      <circle cx="31.1" cy="9.5" r="0.9" fill={INK} />
      <Sparkle x={41} y={40} s={2.6} opacity={0.75} />
    </svg>
  );
}

/** The fallback: a warm constellation of sparkles, so nothing feels unmapped. */
function SparkleCharm() {
  const u = useId();
  return (
    <svg {...svgProps}>
      <Halo id={`${u}h`} />
      <Lin id={`${u}a`} from={GOLD_LT} to={GOLD_DEEP} />
      <Sparkle x={23} y={24} s={14.5} fill={`url(#${u}a)`} opacity={1} />
      <ellipse
        cx="19.8"
        cy="19.5"
        rx="1.7"
        ry="2.6"
        transform="rotate(30 19.8 19.5)"
        fill={CREAM}
        opacity="0.65"
      />
      <Sparkle x={37.5} y={11.5} s={4.4} opacity={0.85} />
      <Sparkle x={11} y={38.5} s={3.2} opacity={0.7} />
    </svg>
  );
}

/* ---- lookup -------------------------------------------------------------- */

const CHARMS: Record<string, () => React.JSX.Element> = {
  star: StarCharm,
  moon: MoonCharm,
  planet: PlanetCharm,
  space: PlanetCharm,
  rocket: RocketCharm,
  spaceship: RocketCharm,
  heart: HeartCharm,
  flower: FlowerCharm,
  fish: FishCharm,
  tree: TreeCharm,
  forest: TreeCharm,
  mountain: MountainCharm,
  hill: MountainCharm,
  train: TrainCharm,
  dinosaur: DinoCharm,
  dino: DinoCharm,
  dragon: DinoCharm,
  fox: PetCharm,
  cat: PetCharm,
  dog: PetCharm,
  puppy: PetCharm,
  kitten: PetCharm,
  wolf: PetCharm,
  wolve: PetCharm, // "wolves" loses only its final s in normalization
  bunny: PetCharm,
  rabbit: PetCharm,
  bear: PetCharm,
  butterfly: ButterflyCharm,
};

/* Unmapped interests share the "wonder" charms, varied deterministically by
   the word itself so five unknown loves don't all wear the same sparkle. */
const FALLBACKS = [SparkleCharm, StarCharm, MoonCharm];

/**
 * One interest → one gilded charm. Same key normalization as the old glyphs
 * (lowercase, letters only, singular) so existing story JSON keeps mapping.
 */
export function Charm({ interest }: { interest: string }) {
  const key = interest.toLowerCase().replace(/[^a-z]/g, "").replace(/s$/, "");
  let Found = CHARMS[key];
  if (!Found) {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 997;
    Found = FALLBACKS[h % FALLBACKS.length];
  }
  return <Found />;
}
